import { ThumbnailCardStyles, variantConfig } from "@config/thumbnailCard";
import { LabelCardIconVariety } from "@elements/Icon/LabelCardIcon";
import LabelCardIconNew from "@elements/Icon/LabelCardIconNew";
import Date from "@elements/Typography/Date";
import { twMerge } from "tailwind-merge";

export type ThumbnailProps<T> = T & {
  id?: string;
  description: string;
  name: string;
  published_at: string;
  nowPlaying: boolean;
  thumbnail: string;
  formattedTime?: string | undefined;
  variant: "video" | "podcast" | "webinar" | "kaki_makan_page";
  handleOnClickThumbnail: (props: T) => void;
  webinar_time?: string;
  testId?: string;
  styles?: ThumbnailCardStyles; // override layer
  isPremium?: boolean;
  poster?: string;
};

export default function ThumbnailCard<T>(props: ThumbnailProps<T>) {
  const {
    description,
    name,
    published_at,
    nowPlaying,
    thumbnail,
    formattedTime,
    variant,
    handleOnClickThumbnail,
    webinar_time,
    testId = "thumbnail-card",
    styles = {},
    isPremium,
    poster,
  } = props;

  const config = variantConfig[variant];
  const {
    Wrapper,
    Poster,
    TimeElement,
    showPremium,
    showDescription,
    showName = true,
  } = config;

  // Merge variant defaults with props.styles
  const mergedStyles: ThumbnailCardStyles = {
    outerWrapperStyle: twMerge(
      config.styles.outerWrapperStyle,
      styles.outerWrapperStyle,
    ),
    buttonStyle: twMerge(config.styles.buttonStyle, styles.buttonStyle),
    wrapperStyle: twMerge(config.styles.wrapperStyle, styles.wrapperStyle),
  };

  const isKakiMakan = variant === "kaki_makan_page";
  const dateClass = isKakiMakan
    ? "hidden lg:block !font-regular leading-[150%]"
    : "";

  const wrapperProps =
    Wrapper === "button"
      ? {
          onClick: () => {
            handleOnClickThumbnail(props);
          },
        }
      : {};

  const nowPlayingOverlay = nowPlaying && isKakiMakan;
  const posterDimensions = isKakiMakan && {
    style: "w-full max-w-[300px]",
    width: 167,
    height: 297,
  };

  let posterSource = thumbnail;
  if (isKakiMakan && poster) {
    posterSource = poster;
  }

  return (
    <div
      data-testid={testId}
      data-variant={variant}
      className={twMerge(
        "flex py-2 lg:py-4 gap-4",
        mergedStyles.outerWrapperStyle,
      )}
    >
      <button
        data-testid={`${testId}-button`}
        onClick={() => {
          handleOnClickThumbnail(props);
        }}
        className={twMerge("flex relative", mergedStyles.buttonStyle)}
      >
        <Poster
          src={posterSource}
          formattedTime={formattedTime}
          posterDimensions={posterDimensions || undefined}
        />
        {nowPlayingOverlay && (
          <div className="absolute top-0 left-0 w-full h-full bg-[rgba(0,0,0,0.5)] backdrop-blur-[3px] flex items-center justify-center">
            <span className="text-white-200 text-xs font-semibold leading-[150%]">
              Kini dimainkan
            </span>
          </div>
        )}
      </button>

      <Wrapper
        {...wrapperProps}
        className={twMerge(
          "flex flex-col justify-start w-full",
          mergedStyles.wrapperStyle,
        )}
      >
        {/* Title + Description */}
        {(showName || showDescription) && (
          <div>
            {isPremium && showPremium && (
              <LabelCardIconNew
                contentAccess={LabelCardIconVariety.PREMIUM}
                className="mb-2"
              />
            )}
            {showName && (
              <h2
                className={twMerge(
                  "text-xs lg:text-sm font-secondary font-[600] text-black-800 leading-xs text-start mb-xs",
                  variant === "podcast" && "lg:text-[16px]",
                  variant === "kaki_makan_page" && "text-sm leading-[120%]",
                )}
                data-testid="name"
              >
                {name}
              </h2>
            )}
            {showDescription && (
              <p
                className={twMerge(
                  "text-xs hidden sm:block self-start text-grey-800 leading-[140%] text-left",
                  variant === "webinar" && "lg:mb-1",
                )}
                data-testid="description"
              >
                {description}
              </p>
            )}
          </div>
        )}

        {/* Date + Now Playing */}
        <div
          className={twMerge(
            "flex flex-col-reverse items-end justify-between w-full lg:items-center lg:flex-row",
            variant === "webinar" && "flex-col lg:order-1",
          )}
        >
          <Date
            className={`text-black-700 text-xs font-semimedium self-start lg:items-center ${dateClass}`}
            dataTestId="date"
            variant="default"
          >
            {published_at}
            {webinar_time && TimeElement && (
              <TimeElement publishTime={webinar_time} />
            )}
          </Date>
          {nowPlaying && !nowPlayingOverlay && (
            <span className="text-blue-400 text-xxs" data-testid="nowPlaying">
              Kini dimainkan
            </span>
          )}
        </div>
      </Wrapper>
    </div>
  );
}
